import { REQUIRED_MESSAGE } from "@/utils/constant";
import { formatDownloadDate } from "@/utils/helper/formatDate";
import { axiosInstance } from "@/utils/lib/axios";
import {
  QueryFunctionContext,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { Paginated } from "./types";
import { generateParams } from "@/utils/helper/generateParams";
import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { CheckingProfileData } from "./master-profile";

const rfsPayloadSchema = z.object({
  cityTown: z.string().optional(),
  clusterId: z.string().optional(),
  clusterIdAbd: z.string().optional(),
  combineStatus: z.string().optional(),
  drawingIdAbd: z.string().optional(),
  drawingIdApd: z.string().optional(),
  fdtId: z.string().optional(),
  media: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
    search: z.string().optional(),
    path: z.string().min(1, { message: REQUIRED_MESSAGE }),
    originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    name: z.string().min(1, { message: REQUIRED_MESSAGE }),
    type: z.string().optional(),
  }),
  notes: z.string().optional(),
  odnType: z.string().min(1, { message: REQUIRED_MESSAGE }),
  oltId: z.string().optional(),
  oltType: z.string().optional(),
  permitId: z.string().optional(),
  projectDescription: z.string().optional(),
  projectId: z.string().min(1, { message: REQUIRED_MESSAGE }),
  projectName: z.string().optional(),
  search: z.string().optional(),
  statusRfsSubmission: z.string().optional(),
  totalBizzHomepass: z
    .number()
    .or(
      z.string().min(1, {
        message: REQUIRED_MESSAGE,
      }),
    )
    .pipe(z.coerce.number()),
  totalHpRfs: z
    .number()
    .or(
      z
        .string()
        .min(1, {
          message: REQUIRED_MESSAGE,
        })
        .optional(),
    )
    .pipe(z.coerce.number())
    .optional(),
  totalResidenceHomepass: z
    .number()
    .or(
      z.string().min(1, {
        message: REQUIRED_MESSAGE,
      }),
    )
    .pipe(z.coerce.number()),
  trackerdataApd: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
  generatedAbds: z
    .array(
      z
        .object({
          id: z.string().min(1, { message: REQUIRED_MESSAGE }),
        })
        .optional(),
    )
    .optional(),
  dateFeederDone: z.string().optional(),
  dateOltDone: z.string().optional(),
  remarks: z.string().optional(),
  fdtType: z.string().min(1, { message: REQUIRED_MESSAGE }),
  category: z.string().min(1, { message: REQUIRED_MESSAGE }),
});
type RFSPayloadSchema = z.infer<typeof rfsPayloadSchema>;

type RFSData = {
  id: string;
  cityTown: string;
  clusterId: string;
  clusterIdAbd: string;
  combineStatus: string;
  drawingIdAbd: string;
  drawingIdApd: string;
  fdtId: string;
  media: {
    id: string;
    search: string;
    path: string;
    originalFileName: string;
    name: string;
    type: string;
  };
  notes: string;
  odnType: string;
  oltId: string;
  oltType: string;
  permitId: string;
  projectDescription: string;
  projectId: string;
  projectName: string;
  status: string;
  statusRfsSubmission: string;
  totalBizzHomepass: number;
  totalHpRfs: number;
  tenantName: string;
  vendor: string;
  totalResidenceHomepass: number;
  totalHomepassInvalid: number;
  totalHomepassAdditional: number;
  trackerdataApd: {
    id: string;
    hpPermit: string;
    hpDesign: string;
    tenantName: string;
    vendor: string;
    latitude: string;
    longitude: string;
    checkingProfile: CheckingProfileData;
  };
  trackerdataSap: {
    id: string;
  };
  picVendor: {
    id: string;
    username: string;
    company: string;
    companyCode: string;
  };
  picLinknet: {
    id: string;
    username: string;
    company: string;
  };
  remarks: string;
  requestId: string;
  dateFeederDone: string;
  dateOltDone: string;
  serviceLogs: ServiceLogData[];
  statusRfsSubmissionLms: StatusRFSSubmissionLmsData;
  documentAbd: string;
  object: RFSData;
  resultTotalResidenceHp: number;
  resultTotalBizzHp: number;
  resultTotalHpRfs: number;
  resultRemarks: string;
  resultDate: string;
  documentMaps: string;
  originalFileName: string;
  opmStatus: string | null;
  opmIsSubmitted: boolean;
  opmCompletedDate: string | null;
  opmUncompletedDate: string | null;
  evidenceNnStatus: string | null;
  evidenceNnIsSubmitted: boolean;
  evidenceNnCompletedDate: string | null;
  evidenceNnUncompletedDate: string | null;
  gponStatus: string | null;
  gponIsSubmitted: boolean;
  gponCompletedDate: string | null;
  gponUncompletedDate: string | null;
  abdFromKmzStatus?: string | null;
  abdFromKmzCompletedDate?: string | null;
  abdFromKmzUncompletedDate?: string | null;
  abdFromDwgStatus?: string | null;
  abdFromDwgCompletedDate?: string | null;
  abdFromDwgUncompletedDate?: string | null;
  abdStatus?: string | null;
  abdCompletedDate?: string | null;
  abdUncompletedDate?: string | null;
  evidenceFeederStatus?: string | null;
  evidenceFeederCompletedDate?: string | null;
  evidenceFeederUncompletedDate?: string | null;
  bakpStatus?: string | null;
  bakpCompletedDate?: string | null;
  bakpUncompletedDate?: string | null;
  cmldStatus?: string | null;
  cmldCompletedDate?: string | null;
  cmldUncompletedDate?: string | null;
  clampHookStatus?: string | null;
  clampHookCompletedDate?: string | null;
  clampHookUncompletedDate?: string | null;
  evidenceOltStatus?: string | null;
  evidenceOltCompletedDate?: string | null;
  evidenceOltUncompletedDate?: string | null;
  generateStatus: string;
  cancelReason: string | null;
  fdtType: string;
  category: string;
};

type ServiceLogData = {
  id: string;
  actBy: string;
  remarks: string;
};

type StatusRFSSubmissionLmsData = {
  id: string;
  value: string;
  paramType: string;
};

/* ================== *
 * Convert KMZ to KML *
 * ================== */

const rfsKmlPayloadSchema = z.object({
  id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  path: z.string().min(1, { message: REQUIRED_MESSAGE }),
  originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
  name: z.string().min(1, { message: REQUIRED_MESSAGE }),
  type: z.string().optional(),
});
type RFSKmlPayloadSchema = z.infer<typeof rfsKmlPayloadSchema>;

async function convertKmzToKml(
  data: RFSKmlPayloadSchema,
): Promise<RFSKmlPayloadSchema> {
  const response = await axiosInstance.post("/bpmn/Rfs/generate-kml", data);
  return response.data;
}

function useRfsKmlConvert() {
  return useMutation({
    mutationFn: ({ data }: { data: RFSKmlPayloadSchema }) =>
      convertKmzToKml(data),
  });
}

/* ========================= *
 * CREATE RFS and start BPMN *
 * ========================= */

interface RFSParams {
  withVariable?: "true" | "false";
}
async function createStartRFS(
  data: RFSPayloadSchema,
  params?: RFSParams,
): Promise<RFSData> {
  const response = await axiosInstance.post(
    "/bpmn/Rfs" + `?withVariable=${params?.withVariable ?? ""}`,
    data,
  );
  return response.data.data;
}

function useRFSCreateStart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      params,
    }: {
      data: RFSPayloadSchema;
      params?: RFSParams;
    }) => createStartRFS(data, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
    },
  });
}

/* ========================= *
 * REVISE RFS                *
 * ========================= */

async function reviseRFS(
  id: string,
  data: RFSPayloadSchema,
  params?: RFSParams,
): Promise<RFSPayloadSchema> {
  const response = await axiosInstance.post(
    "/bpmn/Rfs/revisiRfsdoc/" +
      id +
      "/submit" +
      `?withVariable=${params?.withVariable ?? ""}`,
    data,
  );
  return response.data.data;
}

function useRFSRevise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      params,
    }: {
      id: string;
      data: RFSPayloadSchema;
      params?: RFSParams;
    }) => reviseRFS(id, data, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfss", "rfsInvalids"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["rfsInvalids"] });
    },
  });
}

/* ========================= *
 * CANCEL REVISE RFS                *
 * ========================= */

async function cancelReviseRFS(
  id: string,
  data: { cancelReason: string },
  params: { withVariable: "true" | "false"; decision: "yes" | "no" },
): Promise<RFSPayloadSchema> {
  const response = await axiosInstance.post(
    "/bpmn/Rfs/revisiRfsdoc/" + id + "/submit" + `${generateParams(params)}`,
    data,
  );
  return response.data.data;
}

function useRFSCancelRevise() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      params,
    }: {
      id: string;
      data: { cancelReason: string };
      params: { withVariable: "true" | "false"; decision: "yes" | "no" };
    }) => cancelReviseRFS(id, data, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfss", "rfsInvalids"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["rfsInvalids"] });
    },
  });
}

/* =========================== *
 * Submit Supporting Documents *
 * =========================== */

const submitSupportingDocumentsPayloadSchema = z.object({
  supportingDocument: z.object({
    documents: z.array(
      z.object({
        id: z.string().optional(),
        search: z.string().optional(),
        path: z.string().optional(),
        originalFileName: z.string().optional(),
        name: z.string().optional(),
        type: z.string().optional(),
        entityId: z.string().optional(),
      }),
    ),
    statusFeederAndOlt: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});
type SubmitSupportingDocumentsPayloadSchema = z.infer<
  typeof submitSupportingDocumentsPayloadSchema
>;
interface SubmitSupportingDocumentsParams {
  decision?: "yes" | "no";
  withVariable?: "true" | "false";
}
async function submitSupportingDocuments(
  id: string,
  params: SubmitSupportingDocumentsParams,
  data: SubmitSupportingDocumentsPayloadSchema,
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/submit` +
      `?decision=${params.decision ?? ""}` +
      `&withVariable=${params.withVariable ?? ""}`,
    data,
  );
  return response.data;
}
function useSupportingDocumentsSubmit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      params,
      data,
    }: {
      id: string;
      params: SubmitSupportingDocumentsParams;
      data: SubmitSupportingDocumentsPayloadSchema;
    }) => submitSupportingDocuments(id, params, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
    },
  });
}

/* =========================== *
 *        Cancel RFS           *
 * =========================== */

interface SubmitCancelParams {
  decision?: "yes" | "no";
}
async function submitCancel(
  id: string,
  params: SubmitCancelParams,
  data: { cancelReason: string },
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/submit` +
      `?decision=${params.decision ?? ""}`,
    data,
  );
  return response.data;
}
function useCancelRFS() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      params,
      data,
    }: {
      id: string;
      params: SubmitCancelParams;
      data: { cancelReason: string };
    }) => submitCancel(id, params, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
    },
  });
}

/* =========================== *
 *        Process HPDB           *
 * =========================== */
const submitProcessHPDBPayloadSchema = z.object({
  notes: z.string().optional(),
});
type SubmitProcessHPDBPayloadSchema = z.infer<
  typeof submitProcessHPDBPayloadSchema
>;
interface SubmitProcessHPDB {
  decision: "yes" | "no";
  withVariable: "true" | "false";
}
async function submitProcessHPDB(
  id: string,
  data: SubmitProcessHPDBPayloadSchema,
  params?: SubmitProcessHPDB,
): Promise<RFSData> {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/processsendhpm/${id}/submit?decision=${
      params?.decision ?? ""
    }&withVariable=${params?.withVariable ?? ""}`,
    data,
  );
  return response.data.data;
}

function useSubmitProcessHPDB() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      params,
    }: {
      id: string;
      data: SubmitProcessHPDBPayloadSchema;
      params: SubmitProcessHPDB;
    }) => submitProcessHPDB(id, data, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hpdbs"] });
    },
  });
}

/* =========== *
 * Sync Status *
 * =========== */
async function syncRfsStatus(id: string) {
  const response = await axiosInstance.post(`/bpmn/Rfs/${id}/syncStatus`);
  return response.data;
}

function useSyncRfsStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncRfsStatus(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
    },
  });
}

/* =========================== *
 * Review Supporting Documents *
 * =========================== */

const reviewSupportingDocumentsPayloadSchema = z.object({
  notes: z.string().optional(),
  cancelReason: z.string().optional().nullable(),
  opmFdt: z.object({
    revise: z.boolean(),
  }),
  nnEvidence: z.object({
    revise: z.boolean(),
  }),
  hptar: z.object({
    revise: z.boolean(),
  }),
  gpon: z.object({
    revise: z.boolean(),
  }),
  supportingDocument: z.object({
    abdDariKmzIsRevise: z.boolean(),
    abdDariDwgIsRevise: z.boolean(),
    abdDwgIsRevise: z.boolean(),
    evidenceFeederIsRevise: z.boolean(),
    bakpIsRevise: z.boolean(),
    cmldIsRevise: z.boolean(),
    clampHookIsRevise: z.boolean(),
    evidenceOltIsRevise: z.boolean(),
  }),
});
type ReviewSupportingDocumentsPayloadSchema = z.infer<
  typeof reviewSupportingDocumentsPayloadSchema
>;
interface ReviewSupportingDocumentsParams {
  decision?: "yes" | "no" | "cancel";
  withVariable?: "true" | "false";
}

async function reviewSupportingDocuments(
  id: string,
  data: ReviewSupportingDocumentsPayloadSchema,
  params: ReviewSupportingDocumentsParams,
) {
  if (params.decision === "cancel") {
    data.cancelReason = data.notes;
  }

  const response = await axiosInstance.post(
    `/bpmn/Rfs/reviewGponDocRfs/${id}/submit` +
      `?decision=${params.decision ?? ""}` +
      `&withVariable=${params.withVariable ?? ""}`,
    data,
  );
  return response.data;
}
function useSupportingDocumentsReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      params,
    }: {
      id: string;
      data: ReviewSupportingDocumentsPayloadSchema;
      params: ReviewSupportingDocumentsParams;
    }) => reviewSupportingDocuments(id, data, params),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
    },
  });
}

/* ======== *
 * READ RFS *
 * ======== */

type RFSQueryKey = DataConfig & {
  id: string;
};
interface RfsList extends Paginated {
  content: RFSData[];
}

async function getRFSs(
  queryFunctionContext: QueryFunctionContext,
): Promise<RfsList> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsQueryKey] = queryKey;
  const params = generateParams(rfsQueryKey as DataConfig);
  const response = await axiosInstance.get("/bpmn/Rfs" + params);
  return response.data.data;
}
function useRFSs(
  rfsQueryKey: DataConfig = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["rfss", rfsQueryKey],
    queryFn: getRFSs,
  });
}

async function getRFS(
  queryFunctionContext: QueryFunctionContext,
): Promise<RFSData> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsQueryKey] = queryKey;
  const { id } = rfsQueryKey as RFSQueryKey;

  const response = await axiosInstance.get(`/bpmn/Rfs/${id}`);
  return response.data.data.object;
}

function useRFS(
  rfsQueryKey: RFSQueryKey,
  options?: {
    staleTime?: number;
    refetchInterval?: (query: { state: { data?: RFSData } }) => number | false;
  },
) {
  const { id } = rfsQueryKey;
  return useQuery({
    ...options,
    queryKey: ["rfss", { id }],
    queryFn: getRFS,
  });
}

interface RFSStatusData {
  trackerApdId: string | null;
  opmStatus: string | null;
  evidenceNnStatus: string | null;
  gponStatus: string | null;
  statusRfsSubmission: string | null;
  statusRfsSubmissionLmsValue: string | null;
}

async function getRFSStatus(
  queryFunctionContext: QueryFunctionContext,
): Promise<RFSStatusData> {
  const { queryKey } = queryFunctionContext;
  const [, rfsQueryKey] = queryKey;
  const { id } = rfsQueryKey as RFSQueryKey;

  const response = await axiosInstance.get(`/bpmn/Rfs/${id}/status`);
  return response.data.data;
}

// Key is nested under ["rfss", { id }] so existing RFS invalidations refetch it too.
function useRFSStatus(rfsQueryKey: RFSQueryKey) {
  const { id } = rfsQueryKey;
  return useQuery({
    queryKey: ["rfss", { id }, "status"],
    queryFn: getRFSStatus,
    enabled: !!id,
  });
}

/* ================ *
 * RFS Status Count *
 * ================ */

interface RFSStatusCountValue {
  total: number;
  totalHpRfs: number;
}

interface RFSStatusCount {
  "In Review": RFSStatusCountValue;
  "Prepare & Validate": RFSStatusCountValue;
  "Rejected by Tenant": RFSStatusCountValue;
  "Approved by Tenant": RFSStatusCountValue;
  "Generate HPDB": RFSStatusCountValue;
  "Draft (Invalid)": RFSStatusCountValue;
  "On Review by Tenant": RFSStatusCountValue;
  Cancelled: RFSStatusCountValue;
  Rejected: RFSStatusCountValue;
  "Waiting RFS Submission": RFSStatusCountValue;
  "Total Rfs Submission": RFSStatusCountValue;
  "Total Bucket Tenant": RFSStatusCountValue;
  "Total Bucket Linknet": RFSStatusCountValue;
  "Submit to Tenant": RFSStatusCountValue;
  "Prepare Submit To Tenant": RFSStatusCountValue;
  "Rejected by Tenant to Linknet": RFSStatusCountValue;
  "Rejected by Tenant to Vendor": RFSStatusCountValue;
  "Total Bucket Vendor": RFSStatusCountValue;
  "Processing HPM": RFSStatusCountValue;
}

type RFSStatusCountQueryKey = {
  picLinknet?: string;
  picVendor?: string;
  tenantName?: string;
  vendorName?: string;
  dateMonthYear?: string;
};

async function getRFSStatusCount(
  queryFunctionContext: QueryFunctionContext,
): Promise<RFSStatusCount> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsStatusCountQueryKey] = queryKey;

  const params = generateParams(
    rfsStatusCountQueryKey as RFSStatusCountQueryKey,
  );

  const response = await axiosInstance.get(
    "/bpmn/Rfs/statisticByStatus" + params,
  );
  return response.data.data;
}

function useRFSStatusCount(
  rfsStatusCountQueryKey: RFSStatusCountQueryKey = {},
) {
  return useQuery({
    queryKey: ["rfsStatusCount", { ...rfsStatusCountQueryKey }],
    queryFn: getRFSStatusCount,
  });
}

/* ================== *
 * RFS Trending Count *
 * ================== */

interface RFSTrendingCount {
  OPM: number;
  "Supporting Document": number;
  "NN Evidence": number;
  HPTAR: number;
  GPON: number;
}

type RFSTrendingCountQueryKey = {
  picLinknet?: string;
  picVendor?: string;
  tenantName?: string;
  vendorName?: string;
  dateMonthYear?: string;
};

async function getRFSTrendingCount(
  queryFunctionContext: QueryFunctionContext,
): Promise<RFSTrendingCount> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsTrendingCountQueryKey] = queryKey;

  const params = generateParams(
    rfsTrendingCountQueryKey as RFSTrendingCountQueryKey,
  );

  const response = await axiosInstance.get(
    "/bpmn/Rfs/statisticTrending" + params,
  );
  const data = response.data.data;
  return {
    OPM: data.OPM,
    "Supporting Document": data["Supporting Document"],
    "NN Evidence": data["NN Evidence"],
    HPTAR: data.HPTAR,
    GPON: data.GPON,
  };
}

function useRFSTrendingCount(
  rfsTrendingCountQueryKey: RFSTrendingCountQueryKey = {},
) {
  return useQuery({
    queryKey: ["rfsTrendingCount", { ...rfsTrendingCountQueryKey }],
    queryFn: getRFSTrendingCount,
  });
}

/* =================== *
 * Download RFS xlsx *
 * =================== */

interface RFSDownloadTarget {
  queryParams: string;
}

async function downloadRFSXlsx(target: RFSDownloadTarget): Promise<void> {
  const { queryParams } = target;
  const response = await axiosInstance.post(
    `/bpmn/Rfs/excel${queryParams}`,
    null,
    {
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `RFS_diunduh_pada_${formatDownloadDate()}.xlsx`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function useDownloadRFSXlsx() {
  return useMutation({
    mutationFn: (target: RFSDownloadTarget) => downloadRFSXlsx(target),
  });
}

/* =================== *
 * Generate HPTAR xlsx *
 * =================== */

async function generateHPTARXlsx(id: string, name?: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/hptar/excel/${id}`,
    null,
    {
      responseType: "blob",
    },
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition ? contentDisposition.split("filename=")[1] : `${id}`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
// Using mutation for simplicity in downloading file.
// Ref: https://github.com/TanStack/query/discussions/3105#discussioncomment-1833920
function useGenerateHPTARXlsx() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      generateHPTARXlsx(id, name),
  });
}

/* ============== *
 * Validate HPTAR *
 * ============== */

const hptarValidationSchema = z.object({
  hptar: z.object({
    media: z
      .object({
        id: z.string().min(1, { message: REQUIRED_MESSAGE }),
        path: z.string().min(1, { message: REQUIRED_MESSAGE }),
        originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
        name: z.string().min(1, { message: REQUIRED_MESSAGE }),
      })
      .optional(),
    isHptarNoUpdated: z.boolean().optional(),
  }),
});
type HPTARValidationSchema = z.infer<typeof hptarValidationSchema>;

async function validateHPTAR(id: string, data: HPTARValidationSchema) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateHptar`,
    data,
  );
  return response.data;
}
function useHPTARValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: HPTARValidationSchema }) =>
      validateHPTAR(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptars", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptar-invalids", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-kmz-invalids", { rfsId: id }],
      });
    },
    onError: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptars", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptar-invalids", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-kmz-invalids", { rfsId: id }],
      });
    },
  });
}

/* ============== *
 * Validate Cable Distribution *
 * ============== */

const cableDistributionValidationSchema = z.object({
  hptar: z.object({
    media: z
      .object({
        id: z.string().min(1, { message: REQUIRED_MESSAGE }),
        path: z.string().min(1, { message: REQUIRED_MESSAGE }),
        originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
        name: z.string().min(1, { message: REQUIRED_MESSAGE }),
      })
      .optional(),
  }),
});
type CableDistributionValidationSchema = z.infer<
  typeof cableDistributionValidationSchema
>;

async function validateCableDistribution(
  id: string,
  data: CableDistributionValidationSchema,
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateCableDistribution`,
    data,
  );
  return response.data;
}
function useCableDistributionValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CableDistributionValidationSchema;
    }) => validateCableDistribution(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
      queryClient.invalidateQueries({
        queryKey: ["historyUpdateHomepass", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptars", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptar-invalids", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-kmz-invalids", { rfsId: id }],
      });
    },
    onError: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id }] });
      queryClient.invalidateQueries({
        queryKey: ["historyUpdateHomepass", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptars", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-hptar-invalids", { rfsId: id }],
      });
      queryClient.invalidateQueries({
        queryKey: ["infinite-kmz-invalids", { rfsId: id }],
      });
    },
  });
}

/* ==================== *
 * Validate NN Evidence *
 * ==================== */

const nnEvidenceValidationSchema = z.object({
  nnEvidence: z.object({
    media: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
      name: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
  }),
});
type NNEvidenceValidationSchema = z.infer<typeof nnEvidenceValidationSchema>;

async function validateNNEvidence(
  id: string,
  data: NNEvidenceValidationSchema,
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateNnEvidence`,
    data,
  );
  return response.data;
}

function useNNEvidenceValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: NNEvidenceValidationSchema;
    }) => validateNNEvidence(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["nnEvidences", { rfsId: id }],
      });
    },
  });
}

/* ================ *
 * Validate OPM FDT *
 * ================ */

const opmFDTValidationSchema = z.object({
  opmFdt: z.object({
    id: z.string().optional(),
    fdtId: z.string().min(1, { message: REQUIRED_MESSAGE }),
    fdtIdText: z.string().min(1, { message: REQUIRED_MESSAGE }),
    inputFdtText: z.string().min(1, { message: REQUIRED_MESSAGE }),
    outputFdtText: z.string().min(1, { message: REQUIRED_MESSAGE }),
    imageFdtMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    inputFdtMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    outputFdtMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    poleFdtMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    splitterFdtMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
  }),
});
type OPMFDTValidationSchema = z.infer<typeof opmFDTValidationSchema>;

const opmFATValidationSchema = z.object({
  opmFat: z.object({
    id: z.string().optional(),
    fatId: z.string().min(1, { message: REQUIRED_MESSAGE }),
    outputFatText: z.string().min(1, { message: REQUIRED_MESSAGE }),
    fatIdText: z.string().min(1, { message: REQUIRED_MESSAGE }),
    opmFdt: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    imageFatMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    outputFatMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    poleFatMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
    splitterFatMedia: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
      path: z.string().min(1, { message: REQUIRED_MESSAGE }),
      originalFileName: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
  }),
});
type OPMFATValidationSchema = z.infer<typeof opmFATValidationSchema>;

async function validateOPMFDT(id: string, data: OPMFDTValidationSchema) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateOpmFdt`,
    data,
  );
  return response.data;
}
function useOPMFDTValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OPMFDTValidationSchema }) =>
      validateOPMFDT(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["opmFdts", { rfsId: id }],
      });
    },
  });
}

async function validateOPMFAT(id: string, data: OPMFATValidationSchema) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateOpmFat`,
    data,
  );
  return response.data;
}
function useOPMFATValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OPMFATValidationSchema }) =>
      validateOPMFAT(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["opmFats", { rfsId: id }],
      });
    },
  });
}

interface OPMPoleValidationData {
  opmPole: {
    id?: string;
    poleId: string;
    imagePoleIdMedia?: {
      id?: string;
      path?: string;
      originalFileName?: string;
    };
  };
}

async function validateOPMPole(id: string, data: OPMPoleValidationData) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateOpmPole`,
    data,
  );
  return response.data;
}
function useOPMPoleValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OPMPoleValidationData }) =>
      validateOPMPole(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({
        queryKey: ["opmPoles", { rfsId: id }],
      });
    },
  });
}

interface OPMEvidenceValidationPayload {
  opmEvidence: {
    opmEvidenceType: "olt" | "activity" | "feeder" | "clamphook";
    opmEvidenceMedia: {
      id?: string;
      path?: string;
      originalFileName?: string;
    };
    id?: string; // optional kalau update data
  };
}

async function validateOPMEvidence(
  id: string,
  data: OPMEvidenceValidationPayload,
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/inputGponUploadDoc/${id}/validateOpmEvidence`,
    data,
  );
  return response.data;
}
function useOPMEvidenceValidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: OPMEvidenceValidationPayload;
    }) => validateOPMEvidence(id, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["rfss"] });
      queryClient.invalidateQueries({ queryKey: ["opmEvidences"] });
    },
  });
}

/* ============ *
 * Download OPM *
 * ============ */

async function downloadOPMByRFS(id: string, name?: string) {
  const response = await axiosInstance.get(`/bpmn/Rfs/opm/download/${id}`, {
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.zip`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadOPMByRFS() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      downloadOPMByRFS(id, name),
  });
}

async function downloadOPMZipByRFS(id: string, name?: string) {
  const response = await axiosInstance.get(
    `/bpmn/Rfs/opm/image/download/${id}`,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.zip`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useDownloadOPMZipByRFS() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      downloadOPMZipByRFS(id, name),
  });
}

/* ======== *
 * RFS Logs *
 * ======== */

type RfsLogData = {
  id: string;
  clusterId: string;
  requestId: string;
  createdDate: string;
  actByName: string;
  statusAfter: string;
  statusBefore: string;
  remarks: string;
  totalHpRfs: number;
};

async function getRFSLogs(
  queryFunctionContext: QueryFunctionContext,
): Promise<RfsLogData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsQueryKey] = queryKey;
  const { id } = rfsQueryKey as RFSQueryKey;

  const response = await axiosInstance.get(`/bpmn/Rfs/${id}`);
  return response.data.data.logs;
}

function useRFSLogs(rfsQueryKey: RFSQueryKey) {
  const { id } = rfsQueryKey;
  return useQuery({
    queryKey: ["rfsLogs", { id }],
    queryFn: getRFSLogs,
  });
}

/* =================== *
 * Generate PoleDB xlsx *
 * =================== */

async function generatePoleDbXlsx(id: string, name?: string) {
  const response = await axiosInstance.post(
    `/api/crud/generate-adb-pole/${id}/excel`,
    null,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.xlsx`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function useGeneratePoleDbXlsx() {
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      generatePoleDbXlsx(id, name),
  });
}

/* =================== *
 * RFS Reassign        *
 * =================== */

const reassignPayloadSchema = z.object({
  picLinknet: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});
type ReassignPayloadSchema = z.infer<typeof reassignPayloadSchema>;

async function reassignRFS(
  id: string,
  data: ReassignPayloadSchema,
): Promise<void> {
  const response = await axiosInstance.post(`/bpmn/Rfs/${id}/reassign`, data);
  return response.data;
}

function useRFSReassign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReassignPayloadSchema }) =>
      reassignRFS(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ["rfss", { id }],
      });
    },
  });
}

const reassignBulkPayloadSchema = z.array(
  z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
    picLinknet: z.object({
      id: z.string().min(1, { message: REQUIRED_MESSAGE }),
    }),
  }),
);
type ReassignBulkPayloadSchema = z.infer<typeof reassignBulkPayloadSchema>;

async function reassignBulkRFS(data: ReassignBulkPayloadSchema): Promise<void> {
  const response = await axiosInstance.post("/bpmn/Rfs/reassign/bulk", data);
  return response.data;
}

function useRFSReassignBulk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: ReassignBulkPayloadSchema }) =>
      reassignBulkRFS(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rfss"],
      });
    },
  });
}

/* =================== *
 * Generate RFS Invalid xlsx *
 * =================== */

async function generateRFSInvalidXlxs(id?: string, name?: string) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/rfsInvalid/excel/${id}`,
    null,
    {
      responseType: "blob",
    },
  );
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  const contentDisposition = response.request.getResponseHeader(
    "content-disposition",
  );
  const filename =
    name ??
    (contentDisposition
      ? contentDisposition.split("filename=")[1]
      : `${id}.xlsx`);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
}
function useGenerateRFSInvalidXlsx() {
  return useMutation({
    mutationFn: ({ id, name }: { id?: string; name?: string }) =>
      generateRFSInvalidXlxs(id, name),
  });
}

/* =========== *
 * READ RFS Invalid *
 * =========== */
interface RFSInvalidData {
  id: string;
  key: string;
  message: string;
  rfsId: string;
  valid: boolean;
}
type RFSInvalidQueryKey = DataConfig & {
  rfsId?: string;
};

async function getRFSInvalids(
  queryFunctionContext: QueryFunctionContext,
): Promise<RFSInvalidData[]> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsInvalidQueryKey] = queryKey;
  const { rfsId } = rfsInvalidQueryKey as RFSInvalidQueryKey;

  const response = await axiosInstance.get(
    `/api/crud/rfs-invalid?rfs.id=${rfsId}`,
  );
  return response.data.data.content;
}
function useRFSInvalids(
  rfsInvalidQueryKey: RFSInvalidQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  const { rfsId } = rfsInvalidQueryKey;
  return useQuery({
    ...options,
    queryKey: ["rfsInvalids", { rfsId }],
    queryFn: getRFSInvalids,
  });
}

/* ========================= *
 * CREATE RFS and start BPMN *
 * ========================= */

interface RFSStatusParams {
  id: string;
  withVariable?: "true" | "false";
}

interface RFSStatusPayload {
  opmStatus?: string | null;
  opmIsSubmitted?: boolean;
  opmCompletedDate?: string | null;
  opmUncompletedDate?: string | null;
  evidenceNnStatus?: string | null;
  evidenceNnIsSubmitted?: boolean;
  evidenceNnCompletedDate?: string | null;
  evidenceNnUncompletedDate?: string | null;
  gponStatus?: string | null;
  gponIsSubmitted?: boolean;
  gponCompletedDate?: string | null;
  gponUncompletedDate?: string | null;
}

async function updateRfsStatus(
  data: RFSStatusPayload,
  params?: RFSStatusParams,
) {
  const response = await axiosInstance.post(
    `/bpmn/Rfs/${params?.id}/status` +
      `?withVariable=${params?.withVariable ?? ""}`,
    data,
  );
  return response.data.data;
}

// The status endpoint replaces the whole status snapshot, so two updates that
// run at the same time can overwrite each other with stale values. Updates are
// queued per RFS to make sure they hit the API one after another.
const rfsStatusQueue = new Map<string, Promise<unknown>>();

function enqueueRfsStatusUpdate<T>(
  id: string,
  task: () => Promise<T>,
): Promise<T> {
  const previous = rfsStatusQueue.get(id) ?? Promise.resolve();
  const result = previous.then(task);
  const tail = result.catch(() => undefined);
  rfsStatusQueue.set(id, tail);
  tail.then(() => {
    if (rfsStatusQueue.get(id) === tail) {
      rfsStatusQueue.delete(id);
    }
  });
  return result;
}

function useRFSStatusUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      data,
      params,
      build,
    }: {
      data: RFSStatusPayload;
      params?: RFSStatusParams;
      /**
       * Builds the payload from the latest RFS data. When given, the latest RFS
       * is fetched right before sending, so the snapshot never carries stale
       * values from another update. `data` is the fallback if the fetch fails.
       */
      build?: (latest: RFSData) => RFSStatusPayload;
    }) =>
      enqueueRfsStatusUpdate(params?.id ?? "", async () => {
        let latest: RFSData | undefined;
        if (build && params?.id) {
          try {
            latest = await queryClient.fetchQuery({
              queryKey: ["rfss", { id: params.id }],
              queryFn: getRFS,
              staleTime: 0,
            });
          } catch (error) {
            console.warn("Failed to refresh RFS before status update", error);
          }
        }
        return updateRfsStatus(build && latest ? build(latest) : data, params);
      }),
    onSuccess: (_, { params }) => {
      queryClient.invalidateQueries({ queryKey: ["rfss", { id: params?.id }] });
    },
  });
}

// Definisikan tipe response
interface ApiResponseRegenerate {
  data: "IN_PROGRESS" | "SUCCESS" | "FAILED";
}

/**
 * Memanggil API regenerate.
 * @param id - ID untuk Bpmn
 */
const callRegenerateApi = async (
  id: string,
): Promise<ApiResponseRegenerate> => {
  // Ganti dengan metode yang benar (misal: axios.post)
  const { data } = await axiosInstance.post<ApiResponseRegenerate>(
    `/bpmn/Rfs/${id}/regenerate`,
  );

  return data;
};

// TypeScript
// Add to `src/api/rfs.ts`

const createRfsHomepassInvalidPayloadSchema = z.object({
  reasonHomepass: z.string().optional(),
  qty: z.number().or(z.string().regex(/^\d+$/)).pipe(z.coerce.number()),
  trackerdataApd: z.object({
    id: z.string().min(1, { message: REQUIRED_MESSAGE }),
  }),
});
type CreateRfsHomepassInvalidPayload = z.infer<
  typeof createRfsHomepassInvalidPayloadSchema
>;

type CreateRfsHomepassInvalidPayloadArray = CreateRfsHomepassInvalidPayload[];

async function createRfsHomepassInvalid(
  data: CreateRfsHomepassInvalidPayloadArray,
) {
  const response = await axiosInstance.post(
    "/api/crud/rfsHomepassInvalid",
    data,
  );
  return response.data;
}

function useRFSHomepassInvalidCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    // Accept either the raw array or an object { data: array }
    mutationFn: (
      arg:
        | CreateRfsHomepassInvalidPayloadArray
        | { data: CreateRfsHomepassInvalidPayloadArray },
    ) => {
      const payload = Array.isArray(arg)
        ? arg
        : (arg as { data: CreateRfsHomepassInvalidPayloadArray }).data;
      return createRfsHomepassInvalid(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfsInvalids"] });
    },
  });
}

async function createRfsHomepassAdditional(
  data: CreateRfsHomepassInvalidPayloadArray,
) {
  const response = await axiosInstance.post(
    "/api/crud/rfsHomepassAdditional",
    data,
  );
  return response.data;
}

function useRFSHomepassAdditionalCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    // Accept either the raw array or an object { data: array }
    mutationFn: (
      arg:
        | CreateRfsHomepassInvalidPayloadArray
        | { data: CreateRfsHomepassInvalidPayloadArray },
    ) => {
      const payload = Array.isArray(arg)
        ? arg
        : (arg as { data: CreateRfsHomepassInvalidPayloadArray }).data;
      return createRfsHomepassAdditional(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfsAdditionals"] });
    },
  });
}

interface ReasonHomepassItemResponse {
  id: string;
  reasonHomepass: string;
  qty: number;
}

async function getHomepassInvalid(
  queryFunctionContext: QueryFunctionContext,
): Promise<{ content: ReasonHomepassItemResponse[] }> {
  const { queryKey } = queryFunctionContext;
  const [_, params] = queryKey as [string, { apdId?: string; gapHp?: number }?];
  const { apdId, gapHp } = params ?? {};

  if (!apdId) return { content: [] };

  const url = `/api/crud/rfsHomepassInvalid?trackerdataApd.id=${encodeURIComponent(
    String(apdId),
  )}&gapHp=${encodeURIComponent(String(gapHp ?? ""))}`;

  const response = await axiosInstance.get(url);
  const data = response.data.data;
  return data ?? { content: [] };
}

function useHomepassInvalid(
  params: { apdId?: string; gapHp?: number } = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ["homepassInvalid", params],
    queryFn: getHomepassInvalid,
    ...options,
  });
}

async function getHomepassAdditional(
  queryFunctionContext: QueryFunctionContext,
): Promise<{ content: ReasonHomepassItemResponse[] }> {
  const { queryKey } = queryFunctionContext;
  const [_, params] = queryKey as [string, { apdId?: string; gapHp?: number }?];
  const { apdId, gapHp } = params ?? {};

  if (!apdId) return { content: [] };

  const url = `/api/crud/rfsHomepassAdditional?trackerdataApd.id=${encodeURIComponent(
    String(apdId),
  )}&gapHp=${encodeURIComponent(String(gapHp ?? ""))}`;

  const response = await axiosInstance.get(url);
  const data = response.data.data;
  return data ?? { content: [] };
}

function useHomepassAdditional(
  params: { apdId?: string; gapHp?: number } = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ["homepassAdditional", params],
    queryFn: getHomepassAdditional,
    ...options,
  });
}

type InfiniteHomepassInvalidQueryKey = DataConfig & {
  apdId?: string;
  gapHp?: string;
};

async function getInfiniteHomepassInvalid(
  queryFunctionContext: QueryFunctionContext,
) {
  const { queryKey, pageParam } = queryFunctionContext;
  const [_, generateABDFATQueryKey] = queryKey;
  const { apdId, gapHp } =
    generateABDFATQueryKey as InfiniteHomepassInvalidQueryKey;

  const url = `/api/crud/rfsHomepassInvalid?trackerdataApd.id=${encodeURIComponent(
    String(apdId),
  )}&gapHp=${encodeURIComponent(String(gapHp ?? ""))}&page=${pageParam}`;

  const response = await axiosInstance.get(url);

  return response?.data?.data;
}
function useInfiniteHomepassInvalid(
  generateABDFATQueryKey: InfiniteHomepassInvalidQueryKey = {},
  { enable }: { enable: boolean },
) {
  const { apdId, gapHp } = generateABDFATQueryKey;
  return useInfiniteQuery({
    enabled: enable && Boolean(apdId),
    queryKey: ["homepassInvalid-infinite", { apdId, gapHp }],
    queryFn: getInfiniteHomepassInvalid,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined;
    },
  });
}

type InfiniteHomepassAdditionalQueryKey = DataConfig & {
  apdId?: string;
  gapHp?: string;
};

async function getInfiniteHomepassAdditional(
  queryFunctionContext: QueryFunctionContext,
) {
  const { queryKey, pageParam } = queryFunctionContext;
  const [_, generateABDFATQueryKey] = queryKey;
  const { apdId, gapHp } =
    generateABDFATQueryKey as InfiniteHomepassAdditionalQueryKey;

  const url = `/api/crud/rfsHomepassAdditional?trackerdataApd.id=${encodeURIComponent(
    String(apdId),
  )}&gapHp=${encodeURIComponent(String(gapHp ?? ""))}&page=${pageParam}`;

  const response = await axiosInstance.get(url);

  return response?.data?.data;
}
function useInfiniteHomepassAdditional(
  generateABDFATQueryKey: InfiniteHomepassAdditionalQueryKey = {},
  { enable }: { enable: boolean },
) {
  const { apdId, gapHp } = generateABDFATQueryKey;
  return useInfiniteQuery({
    enabled: enable && Boolean(apdId),
    queryKey: ["homepassAdditional-infinite", { apdId, gapHp }],
    queryFn: getInfiniteHomepassAdditional,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.number + 1 < lastPage.totalPages
        ? lastPage.number + 1
        : undefined;
    },
  });
}

interface RfsClusterSummary {
  hp: number;
  fdt: number;
  fat: number;
  clampHook: number;
  qspan: number;
  deploymentType: {
    [x: string]: number;
  };
}

async function getRfsClusterSummary(
  queryFunctionContext: QueryFunctionContext,
): Promise<RfsClusterSummary> {
  const { queryKey } = queryFunctionContext;
  const [_, rfsQueryKey] = queryKey;
  const { id } = rfsQueryKey as RFSQueryKey;

  const response = await axiosInstance.get(`/bpmn/Rfs/${id}/clusterSummary`);
  return response.data.data;
}

function useRfsClusterSummary(
  rfsQueryKey: RFSQueryKey,
  options?: { staleTime?: number },
) {
  const { id } = rfsQueryKey;
  return useQuery({
    ...options,
    queryKey: ["cluster-summary-rfs", { id }],
    queryFn: getRfsClusterSummary,
  });
}

// ======================== *
// DELETE Homepass Additional *
// ======================== */
async function deleteHomepassAdditional(id: string) {
  const response = await axiosInstance.delete(
    `/api/crud/rfsHomepassAdditional/${encodeURIComponent(id)}`,
  );
  return response.data;
}

function useDeleteHomepassAdditional() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteHomepassAdditional(id),
    onSuccess: () => {
      // Invalidate queries related to homepass additionals if necessary
      queryClient.invalidateQueries({
        queryKey: ["homepassAdditional-infinite"],
      });
    },
  });
}

// ======================== *
// DELETE Homepass Invalid *
// ======================== */
async function deleteHomepassInvalid(id: string) {
  const response = await axiosInstance.delete(
    `/api/crud/rfsHomepassInvalid/${encodeURIComponent(id)}`,
  );
  return response.data;
}

function useDeleteHomepassInvalid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteHomepassInvalid(id),
    onSuccess: () => {
      // Invalidate queries related to homepass invalids if necessary
      queryClient.invalidateQueries({ queryKey: ["homepassInvalid-infinite"] });
    },
  });
}

export type {
  RFSPayloadSchema,
  RFSKmlPayloadSchema,
  RFSData,
  OPMFDTValidationSchema,
  OPMFATValidationSchema,
  SubmitSupportingDocumentsPayloadSchema,
  ReassignPayloadSchema,
  ApiResponseRegenerate,
  ReasonHomepassItemResponse,
  OPMPoleValidationData,
  OPMEvidenceValidationPayload,
};
export {
  useRfsKmlConvert,
  useRFSCreateStart,
  useRFSs,
  useRFS,
  useRFSStatus,
  useRFSStatusCount,
  useRFSTrendingCount,
  useDownloadRFSXlsx,
  useHPTARValidate,
  useNNEvidenceValidate,
  useGenerateHPTARXlsx,
  useSupportingDocumentsSubmit,
  useSupportingDocumentsReview,
  useRFSLogs,
  useOPMFDTValidate,
  useOPMFATValidate,
  submitSupportingDocumentsPayloadSchema,
  useDownloadOPMByRFS,
  useDownloadOPMZipByRFS,
  useGeneratePoleDbXlsx,
  useRFSRevise,
  useCancelRFS,
  submitCancel,
  reassignPayloadSchema,
  useRFSReassign,
  useRFSReassignBulk,
  useSubmitProcessHPDB,
  useSyncRfsStatus,
  useGenerateRFSInvalidXlsx,
  useRFSInvalids,
  useRFSStatusUpdate,
  useRFSCancelRevise,
  callRegenerateApi,
  useRFSHomepassInvalidCreate,
  useHomepassInvalid,
  useHomepassAdditional,
  useRFSHomepassAdditionalCreate,
  useRfsClusterSummary,
  useCableDistributionValidate,
  useDeleteHomepassAdditional,
  useDeleteHomepassInvalid,
  useInfiniteHomepassInvalid,
  useInfiniteHomepassAdditional,
  useOPMPoleValidate,
  useOPMEvidenceValidate,
};
